/**
 * Circular dependency prevention:
 * - useBasket
 * - useProduct
 */
import { computed, reactive } from 'vue'

import events$ from '@/services/Events'

import { isRequired } from '@/utils/CustomErrors'

import useBasket from '@/hooks/useBasket'
import useDistribution from '@/hooks/useDistribution'
import useLogger from '@/hooks/useLogger'
import useProduct from '@/hooks/useProduct'
import useI18n from '@/hooks/useI18n'

import { SALES_PARTNER_TYPE } from '@/config/constants'
import { EVENT_BASKET } from '@/config/events'

// Hooks
const { getDistributionPartnerNumber } = useDistribution()
const { logInfo } = useLogger()
const { i18n } = useI18n()
let installed = false

const localPartner = reactive({})
let tmpData = {
  email: '{email}',
  phone: '{phone}',
  firstName: '{firstName}',
  lastName: '{lastName}',
}

events$.on(EVENT_BASKET.UPDATED, async ({ basket, originalBasket }) => {
  const { isProductReloadNecessary } = useProduct()
  const doReload =
    isProductReloadNecessary(basket, originalBasket) ||
    getPartnerNumberOfContractOwner(basket) !== getPartnerNumberOfContractOwner(originalBasket)

  if (!basket.basketId) return

  if (doReload || installed === false) {
    await install(basket, getDistributionPartnerNumber(basket.distributionPartners))
    installed = true
  }
})

// Computed
const advisorFullName = computed(() => {
  return `${partner.value.base.lastName} ${partner.value.base.firstName}`
})

const partnerId = computed(() => {
  const { basket } = useBasket()
  return getDistributionPartnerNumber(basket.distributionPartners)
})

const partner = computed(() => {
  const { base, info, contact } = localPartner
  const isMobileDistributor = base?.salesPartnerType === SALES_PARTNER_TYPE.MOBILE

  const content = {}
  if (localPartner.content) {
    if (localPartner.content.distributionPartners[partnerId.value]) {
      content.distributionPartner = localPartner.content.distributionPartners[partnerId.value]
    }

    if (localPartner.content.masterTreaty[localPartner.base.masterTreaty]) {
      content.logoPath = localPartner.content.masterTreaty[localPartner.base.masterTreaty]
    } else {
      Object.assign(content, localPartner.content?.distributionPartners?.[partnerId.value])
    }

    if (isMobileDistributor) {
      content.privateInsurers = localPartner.content.privateInsurers
      content.privateInsurers.intro = content.privateInsurers.intro
        .replace(tmpData.firstName, base.firstName)
        .replace(tmpData.lastName, base.lastName)

      content.privateInsurers.bulletPoints = content.privateInsurers.bulletPoints.map(bullet => {
        return bullet
          .replaceAll(tmpData.email, contact.email?.[i18n.global.locale.value])
          .replaceAll(tmpData.phone, contact.phoneNumber)
      })

      /**
       * in the first iteration {email}/{phone}/{firstName}/{lastName} get's replaced by actual data.
       * to fill in the "correct" one in another passthrough, save the string
       */
      tmpData = {
        email: contact.email?.[i18n.global.locale.value],
        phone: contact.phoneNumber,
        firstName: base.firstName,
        lastName: base.lastName,
      }
    }
  }

  return {
    partnerId: partnerId.value,
    isMobileDistributor,
    isBroker: base?.salesPartnerType === SALES_PARTNER_TYPE.BROKER,
    base,
    info,
    contact,
    content,
  }
})

const isPartner = computed(() => {
  const { basket } = useBasket()

  return !!partner.value.base && !basket.existingCustomer
})

async function install(basket = isRequired('basket'), __partnerId = false) {
  const { fetchDistributionPartner } = useBasket()

  const distriContent = await fetchPartnerContent(basket.language.toLowerCase())
  const distriInfo = await fetchDistributionPartner(basket, getPartnerNumberOfContractOwner(basket))

  await setPartner(Object.assign({}, { content: distriContent }, distriInfo))

  return partner.value
}

function setPartner(payload) {
  Object.assign(localPartner, payload)
  logInfo(['%cPARTNER SET', 'color: green', localPartner])
}

function getPartnerNumberOfContractOwner(__basket) {
  const contractOwnerId = __basket?.contractOwner?.personId
  return __basket.persons?.find(p => p.personId === contractOwnerId)?.partnerNumber
}

async function fetchPartnerContent(language) {
  const content = await import(`@/language/partner/partner.${language}.json`)
  return content.default
}

function hasSalesPartnerEmail(salesPartnerType) {
  return [SALES_PARTNER_TYPE.BROKER, SALES_PARTNER_TYPE.PRIVATE_INSURER].includes(salesPartnerType)
}

export default function usePartner() {
  return {
    advisorFullName,
    partner,
    partnerId,

    install,

    hasSalesPartnerEmail,
    isPartner,

    fetchPartnerContent,

    setPartner,
  }
}
